// AndroidInstallPrompt.jsx — Anamoria SPA
// v1.0 — PWA Install Guide (May 29, 2026)
// Custom install banner for Android/Chromium browsers.
// Captures the `beforeinstallprompt` event (deferred), then shows a banner
// with a real "Install" button that triggers the native install dialog.
// Best practice: do NOT rely on the (engagement-gated) native mini-infobar —
// capture the event and present our own affordance.
// Only renders when: a deferred prompt exists, not already in standalone mode,
// and not previously dismissed. Dismissed state persisted in localStorage.
// Onboarding gating is handled by App.jsx (rendered only when onboardingComplete).
// Accessibility: role="complementary", aria-label, keyboard-dismissible (Escape).

import { useState, useEffect, useCallback } from 'react';
import styles from './AndroidInstallPrompt.module.css';

const DISMISS_KEY = 'ana_android_install_dismissed';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function AndroidInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === 'true') return;

    const onBeforeInstall = (e) => {
      // Prevent the automatic mini-infobar; we present our own UI.
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    const onInstalled = () => {
      localStorage.setItem(DISMISS_KEY, 'true');
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // userChoice may reject on some browsers; safe to ignore.
    }
    // The deferred event can only be used once.
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  // Keyboard: Escape dismisses
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      className={styles.banner}
      role="complementary"
      aria-label="Install instructions"
    >
      <div className={styles.content}>
        <p className={styles.message}>
          Add Anamoria to your Home Screen for the best experience.
        </p>
        <p className={styles.hint}>
          Tap <strong>Install</strong>, or use the browser menu
          {' '}<span aria-hidden="true">⋮</span>{' '}→ <strong>Add to Home screen</strong>.
        </p>
      </div>
      <div className={styles.actions}>
        <button
          className={styles.installBtn}
          onClick={install}
          aria-label="Install Anamoria"
        >
          Install
        </button>
        <button
          className={styles.dismissBtn}
          onClick={dismiss}
          aria-label="Dismiss install prompt"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
